/**
 * The card Seth proposes in the Creative Studio (Gary, 1 Nov 2026). Seth ends a Studio answer with one
 * fenced block:
 *
 *   ```card
 *   { "template": "insight", "label": "Insights", "headline": "No shame,", "accent": "just science.",
 *     "body": "...", "story": "One caption line", "post": { "title": "...", "text": "...", "hashtags": "...", "theme": "learn" } }
 *   ```
 *
 * A carousel gives "slides": [{ "headline", "accent", "body" }, ...] instead of headline/accent/body.
 * Photo cards may carry "imageBrief": Seth's description of the photo, which "Generate image" sends to
 * OpenAI. The photo or partner logo is otherwise picked in the Studio, never by Seth. Pure, so it can be tested.
 */

import type { CardContent, CardKind } from './cards';
import { stripReelBlocks } from './reelSpec';

export type CardDraft = Omit<CardContent, 'url' | 'imageUrl'>;

export interface PostDraft {
  title: string;
  text: string;
  hashtags: string;
  theme: string;
}

export interface StudioSpec {
  template: CardKind;
  cards: CardDraft[];
  /** The story's caption line; no story when empty. */
  story: string;
  /** Photo cards: Seth's short description of the photo, for "Generate image". */
  imageBrief: string;
  post: PostDraft;
}

const TEMPLATES: CardKind[] = ['insight', 'carousel', 'review', 'doctor', 'partner', 'photo'];
const THEMES = ['learn', 'review', 'partner', 'doctor', 'customer', 'behind_scenes', 'nudge'];
const DEFAULT_LABEL: Record<CardKind, string> = {
  insight: 'Insights',
  carousel: 'Insights',
  review: 'Google review',
  doctor: 'Doctors recommend it',
  partner: 'Meet our partner',
  photo: '',
};
const DEFAULT_THEME: Record<CardKind, string> = {
  insight: 'learn', carousel: 'learn', review: 'review', doctor: 'doctor', partner: 'partner', photo: 'nudge',
};

const FENCE = /```card[^\n]*\n([\s\S]*?)(```|$)/g;

/** The last complete card block in an answer, or null when there is none. */
export function lastCardBlock(text: string): string | null {
  let found: string | null = null;
  for (const m of text.matchAll(FENCE)) {
    if (m[2] === '```') found = (m[1] ?? '').trim();
  }
  return found;
}

/** The answer as the chat shows it: each card or reel block, finished or still arriving, becomes one plain line. */
export function stripCardBlocks(text: string): string {
  return stripReelBlocks(text.replace(FENCE, 'Card updated in the preview.'));
}

function str(v: unknown, max = 600): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

/** Reads Seth's block. Anything missing gets the template's default; a broken block says what is wrong. */
export function parseCardSpec(block: string): StudioSpec | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(block);
  } catch {
    return { error: 'Seth sent a card the Studio could not read. Ask him to send it again.' };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { error: 'Seth sent an empty card.' };
  const o = raw as Record<string, unknown>;
  const template = TEMPLATES.find((t) => t === o.template);
  if (!template) return { error: `Seth asked for a template the Studio does not have: ${str(o.template, 40) || 'none'}.` };

  const label = str(o.label, 40) || DEFAULT_LABEL[template];
  const base: CardDraft = { kind: template, label };
  let cards: CardDraft[];

  if (template === 'carousel') {
    const slides = Array.isArray(o.slides) ? o.slides.slice(0, 10) : [];
    if (slides.length < 2) return { error: 'A carousel needs at least 2 slides.' };
    cards = slides.map((s, i) => {
      const slide = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
      return {
        ...base,
        headline: str(slide.headline, 80),
        headlineAccent: str(slide.accent, 80),
        body: str(slide.body),
        slide: { index: i + 1, total: slides.length },
        swipe: i === 0,
      };
    });
  } else {
    cards = [{
      ...base,
      headline: str(o.headline, 80),
      headlineAccent: str(o.accent, 80),
      body: str(o.body),
      quote: str(o.quote),
      name: str(o.name, 80),
      role: str(o.role, 80) || (template === 'review' ? '5 stars on Google' : ''),
      stars: typeof o.stars === 'number' ? Math.max(1, Math.min(5, Math.round(o.stars))) : 5,
      partnerName: str(o.partner, 120),
      boxLead: str(o.boxLead, 60),
      boxBody: str(o.boxBody),
    }];
  }

  const p = (o.post && typeof o.post === 'object' ? o.post : {}) as Record<string, unknown>;
  const theme = THEMES.includes(str(p.theme, 20)) ? str(p.theme, 20) : DEFAULT_THEME[template];
  const firstHeadline = [cards[0]?.headline, cards[0]?.headlineAccent].filter(Boolean).join(' ');
  return {
    template,
    cards,
    story: template === 'carousel' ? '' : str(o.story, 120),
    imageBrief: template === 'photo' ? str(o.imageBrief, 600) : '',
    post: {
      title: str(p.title, 120) || firstHeadline || cards[0]?.partnerName || cards[0]?.name || 'Studio post',
      text: str(p.text, 2200),
      hashtags: str(p.hashtags, 300),
      theme,
    },
  };
}
